// Entregas: a janela lateral com o que cada agente terminou (o turno que acabou com resposta final).
//
// Lista de cartões, do mais novo para o mais velho: avatar, nome, · projeto, há quanto tempo; a
// primeira frase da resposta em destaque, o resto em cinza, miniaturas dos anexos e o rodapé
// (passos · duração). Tocar abre o detalhe: o pedido, a resposta inteira (markdown do terminal ao
// vivo) e os anexos em tamanho cheio.
//
// Selo no botão da barra: entregas que ainda não foram vistas. "Visto" é o instante em que a janela
// abriu pela última vez, guardado em localStorage. Entregas novas chegam pelo SSE (evento
// 'delivery', ligado em main.ts). Só leitura: GET /api/deliveries*. A janela abre também pelo evento
// window 'agzos:painel' com detail 'entregas' (a barra e outros atalhos usam esse).

import { CAST_BY_ID, castFor } from '../core/cast.js';
import type { Look } from '../core/look.js';
import type { Delivery, DeliveryCard } from '../core/types.js';
import { characterFrame } from './engine/character.js';
import { duracaoTurno, onLangChange, plural, t, tempoRelativo } from './i18n.js';
import { markdown } from './live.js';
import { attachBadge, refreshTools } from './toolbar.js';

/** Instante (epoch ms) em que a janela foi aberta pela última vez. */
const VISTO_KEY = 'agzos.entregas.visto';
/** Quantas entregas a janela guarda na lista. */
const LISTA_MAX = 200;

export interface EntregasPainel {
  /** Chegou uma entrega nova (SSE). */
  recebe(card: DeliveryCard): void;
  /** Depois do login: carrega a lista para o selo e marca o início da contagem na primeira visita. */
  inicia(): void;
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/** URL da imagem n de uma entrega (o servidor lê do transcript na hora). */
const anexoUrl = (id: string, n: number): string => `/api/deliveries/${encodeURIComponent(id)}/anexo/${n}`;

/** Cabeça e ombros do personagem, como as listas da página fazem. */
const rostos = new Map<string, string>();
function rostoUrl(card: DeliveryCard): string {
  const look: Look = (CAST_BY_ID.get(card.look) ?? castFor(card.agentId)).look;
  const key = JSON.stringify(look);
  const hit = rostos.get(key);
  if (hit) return hit;
  const frame = characterFrame(look, 'down', 'idle');
  const out = document.createElement('canvas');
  out.width = 16;
  out.height = 16;
  const ctx = out.getContext('2d');
  if (!ctx) return '';
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(frame, 0, 0, 16, 16, 0, 0, 16, 16);
  const url = out.toDataURL();
  rostos.set(key, url);
  return url;
}

/** Casca da janela (textos já traduzidos). Refeita quando o idioma muda. */
function casca(): string {
  return `<header class="entregas-cab">
      <button class="entregas-voltar" type="button" hidden>‹ ${esc(t('entregas.voltar'))}</button>
      <h2 class="entregas-titulo">${esc(t('entregas.titulo'))}</h2>
      <button class="entregas-fechar" type="button" aria-label="${esc(t('acao.fechar'))}" title="${esc(t('acao.fechar'))}">✕</button>
    </header>
    <p class="entregas-explica">${esc(t('entregas.explica'))}</p>
    <p class="entregas-aviso" role="status" hidden></p>
    <ol class="entregas-lista"></ol>
    <div class="entregas-detalhe" hidden></div>`;
}

/** Cartão da lista. */
function cartao(card: DeliveryCard): string {
  const miniaturas = card.attachments
    .map((a) => `<img class="entrega-miniatura" src="${anexoUrl(card.id, a.n)}" alt="${esc(t('entregas.anexo', { n: a.n + 1 }))}" width="72" height="54" loading="lazy" />`)
    .join('');
  return `<li class="entrega">
      <button class="entrega-abrir" type="button" data-id="${esc(card.id)}">
        <span class="entrega-topo">
          <img class="entrega-rosto" src="${rostoUrl(card)}" alt="" width="32" height="32" />
          <span class="entrega-quem"><b>${esc(card.agentName)}</b> <span class="entrega-proj">· ${esc(card.project)}</span></span>
          <time class="entrega-quando" datetime="${new Date(card.endedAt).toISOString()}">${esc(tempoRelativo(card.endedAt))}</time>
        </span>
        <span class="entrega-frase">${esc(card.frase)}</span>
        ${card.resumo ? `<span class="entrega-resumo">${esc(card.resumo)}</span>` : ''}
        ${miniaturas ? `<span class="entrega-anexos">${miniaturas}</span>` : ''}
        <span class="entrega-rodape">${esc(plural('entregas.passos', card.steps))} · ${esc(duracaoTurno(card.durationMs))}</span>
      </button>
    </li>`;
}

/** Entrega inteira: pedido, resposta (markdown) e anexos em tamanho cheio. */
function detalhe(d: Delivery): string {
  const grandes = d.attachments
    .map((a) => {
      const url = anexoUrl(d.id, a.n);
      return `<a class="entrega-grande" href="${url}" target="_blank" rel="noopener" title="${esc(t('entregas.abrir_imagem'))}">
          <img src="${url}" alt="${esc(t('entregas.anexo', { n: a.n + 1 }))}" loading="lazy" />
        </a>`;
    })
    .join('');
  return `<article class="entrega-completa">
      <div class="entrega-topo">
        <img class="entrega-rosto" src="${rostoUrl(d)}" alt="" width="32" height="32" />
        <span class="entrega-quem"><b>${esc(d.agentName)}</b> <span class="entrega-proj">· ${esc(d.project)}</span></span>
        <time class="entrega-quando" datetime="${new Date(d.endedAt).toISOString()}">${esc(tempoRelativo(d.endedAt))}</time>
      </div>
      <h3 class="entrega-rotulo">${esc(t('entregas.pedido'))}</h3>
      <p class="entrega-pedido">${d.ask ? esc(d.ask) : `<i>${esc(t('entregas.sem_pedido'))}</i>`}</p>
      <h3 class="entrega-rotulo">${esc(t('entregas.resposta'))}</h3>
      <div class="entrega-texto">${markdown(d.reply)}</div>
      ${grandes ? `<h3 class="entrega-rotulo">${esc(t('entregas.anexos'))}</h3><div class="entrega-grandes">${grandes}</div>` : ''}
      <p class="entrega-rodape">${esc(plural('entregas.passos', d.steps))} · ${esc(duracaoTurno(d.durationMs))}</p>
    </article>`;
}

/** Monta a janela Entregas, registra o botão na barra e liga o SSE (main.ts chama recebe). */
export function montaEntregas(): EntregasPainel {
  let cards: DeliveryCard[] = [];
  /** Entrega aberta no detalhe (null = lista). */
  let aberta: string | null = null;
  let visivel = false;

  const el = document.createElement('section');
  el.className = 'entregas';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', t('entregas.titulo'));
  el.innerHTML = casca();
  document.body.appendChild(el);

  const lista = (): HTMLElement => el.querySelector<HTMLElement>('.entregas-lista') as HTMLElement;
  const explica = (): HTMLElement => el.querySelector<HTMLElement>('.entregas-explica') as HTMLElement;
  const detalheEl = (): HTMLElement => el.querySelector<HTMLElement>('.entregas-detalhe') as HTMLElement;
  const aviso = (texto: string): void => {
    const p = el.querySelector<HTMLElement>('.entregas-aviso') as HTMLElement;
    p.textContent = texto;
    p.hidden = !texto;
  };

  function pintaLista(): void {
    aberta = null;
    el.querySelector<HTMLElement>('.entregas-voltar')!.hidden = true;
    explica().hidden = false;
    detalheEl().hidden = true;
    lista().hidden = false;
    lista().innerHTML = cards.length ? cards.map(cartao).join('') : '';
    if (!cards.length) aviso(t('entregas.vazio'));
    else aviso('');
  }

  async function abreDetalhe(id: string): Promise<void> {
    aberta = id;
    el.querySelector<HTMLElement>('.entregas-voltar')!.hidden = false;
    explica().hidden = true;
    lista().hidden = true;
    detalheEl().hidden = false;
    detalheEl().innerHTML = '';
    aviso('');
    const response = await fetch(`/api/deliveries/${encodeURIComponent(id)}`).catch(() => null);
    if (aberta !== id) return; // o dono já voltou ou abriu outra
    if (!response || !response.ok) {
      aviso(response?.status === 404 ? t('entregas.sumiu') : t('entregas.erro'));
      return;
    }
    const { delivery } = (await response.json()) as { delivery: Delivery };
    if (aberta !== id) return;
    detalheEl().innerHTML = detalhe(delivery);
  }

  async function carrega(): Promise<void> {
    const response = await fetch('/api/deliveries').catch(() => null);
    if (!response || !response.ok) {
      aviso(t('entregas.erro'));
      return;
    }
    const { items } = (await response.json()) as { items: DeliveryCard[] };
    // a lista do servidor manda; o que chegou pelo SSE enquanto isso e ainda não estava nela fica
    const ids = new Set(items.map((c) => c.id));
    cards = [...items, ...cards.filter((c) => !ids.has(c.id))].sort((a, b) => b.endedAt - a.endedAt).slice(0, LISTA_MAX);
    if (visivel && aberta === null) pintaLista();
  }

  /** Entregas que chegaram depois da última vez que a janela abriu. */
  function naoVistas(): number {
    const visto = lePrefVisto();
    return cards.filter((c) => c.endedAt > visto).length;
  }

  function abre(): void {
    visivel = true;
    el.hidden = false;
    marcaVisto();
    refreshTools();
    if (aberta === null) pintaLista();
    void carrega();
  }

  function fecha(): void {
    visivel = false;
    el.hidden = true;
  }

  function marcaVisto(): void {
    try {
      localStorage.setItem(VISTO_KEY, String(Date.now()));
    } catch {
      // modo privado: o selo vale só nesta aba
    }
  }

  el.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('.entregas-fechar')) return fecha();
    if (target.closest('.entregas-voltar')) return pintaLista();
    const abrir = target.closest<HTMLElement>('.entrega-abrir');
    if (abrir?.dataset.id) void abreDetalhe(abrir.dataset.id);
  });
  document.addEventListener('keydown', (event) => {
    if (visivel && event.key === 'Escape') fecha();
  });
  window.addEventListener('agzos:painel', (event) => {
    if ((event as CustomEvent<string>).detail === 'entregas') (visivel ? fecha() : abre());
  });
  onLangChange(() => {
    const id = aberta;
    el.innerHTML = casca();
    el.setAttribute('aria-label', t('entregas.titulo'));
    if (!visivel) return;
    if (id) void abreDetalhe(id);
    else pintaLista();
  });

  // o botão é da barra (src/web/toolbar.ts); aqui só o selo das entregas não vistas
  attachBadge('entregas', naoVistas);

  return {
    recebe(card) {
      if (!cards.some((c) => c.id === card.id)) cards = [card, ...cards].sort((a, b) => b.endedAt - a.endedAt).slice(0, LISTA_MAX);
      refreshTools(); // selo da barra
      if (visivel && aberta === null) pintaLista();
    },
    inicia() {
      try {
        if (localStorage.getItem(VISTO_KEY) === null) marcaVisto();
      } catch {
        // sem localStorage, a contagem começa do zero a cada carregamento
      }
      void carrega();
    },
  };
}

/** Quando a janela foi aberta pela última vez (0 = nunca). */
function lePrefVisto(): number {
  try {
    const v = Number(localStorage.getItem(VISTO_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}
